import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import './Home.css';

export const Home = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  const handleGetStarted = () => {
    if (authService.isAuthenticated()) {
      navigate('/ai');
    } else {
      navigate('/login');
    }
  };

  const handleLearnPricing = () => {
    navigate('/pricing');
  };

  const features = [
    {
      icon: '⚡',
      title: 'BAIQL Automation',
      subtitle: 'Batch AI Query Language',
      description: 'Script complex AI workflows with our proprietary automation language. Chain multiple models, process datasets, and automate repetitive tasks with simple, powerful commands.',
      highlight: 'Like SQL for AI'
    },
    {
      icon: '🎯',
      title: 'Multi-Model Intelligence',
      subtitle: '11 AI Models, One Platform',
      description: 'Access GPT-4, Claude, Gemini, and 8 other leading AI models through a single interface. Compare outputs, leverage model strengths, and never get locked into one ecosystem.',
      highlight: 'True Model Freedom'
    },
    {
      icon: '🚀',
      title: 'Prompt Optimization',
      subtitle: 'AI-Powered Prompt Engineering',
      description: 'Transform basic prompts into expert-level instructions automatically. Our 6-step generator uses AI to optimize your prompts before execution, guaranteeing professional results.',
      highlight: 'Get It Right First Time'
    },
    {
      icon: '💰',
      title: 'Transparent Pricing',
      subtitle: 'Pay Only For What You Use',
      description: 'Token-based pricing with 40-60% margins built in. No surprises, no subscriptions you forget about. $5 replaces multiple $20/month AI tools.',
      highlight: 'Predictable Costs'
    },
    {
      icon: '🏢',
      title: 'Enterprise Controls',
      subtitle: 'Built For Teams & Organizations',
      description: 'Real-time monitoring, script permissions, PII detection, content moderation, alerting systems, and comprehensive admin dashboards. Security and governance built-in.',
      highlight: 'Enterprise-Ready'
    }
  ];

  return (
    <div className="home-container" style={{
      fontFamily: '"Space Grotesk", -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#0a0a0a',
      color: '#ffffff'
    }}>
      {/* Add custom fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      
      {/* Hero Section - Bold & Distinctive */}
      <section style={{
        minHeight: '95vh',
        background: 'linear-gradient(135deg, #1a0b2e 0%, #2d1b4e 50%, #1a0b2e 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px'
      }}>
        {/* Animated background elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.15,
          background: 'radial-gradient(circle at 20% 50%, #667eea 0%, transparent 50%), radial-gradient(circle at 80% 80%, #764ba2 0%, transparent 50%)',
          animation: 'pulse 8s ease-in-out infinite'
        }} />
        
        <div style={{ maxWidth: '1200px', width: '100%', position: 'relative', zIndex: 1 }}>
          {/* Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
            <img src="/logo.png" alt="PocketPE Logo" style={{ 
              height: '100px', 
              width: 'auto',
              filter: 'drop-shadow(0 4px 20px rgba(102, 126, 234, 0.4))'
            }} />
          </div>

          {/* Main headline */}
          <h1 style={{
            fontSize: 'clamp(2.5rem, 8vw, 5rem)',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '20px',
            lineHeight: '1.1',
            background: 'linear-gradient(135deg, #ffffff 0%, #a78bfa 50%, #667eea 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.02em'
          }}>
            Beyond Prompts.<br />Beyond Chat.<br />Beyond AI.
          </h1>

          {/* Subheadline */}
          <p style={{
            fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
            textAlign: 'center',
            marginBottom: '30px',
            color: '#e0e6ff',
            maxWidth: '800px',
            margin: '0 auto 30px auto',
            lineHeight: '1.4'
          }}>
            The only platform that combines <span style={{ color: '#a78bfa', fontWeight: '600' }}>AI automation</span>, 
            <span style={{ color: '#a78bfa', fontWeight: '600' }}> multi-model access</span>, and 
            <span style={{ color: '#a78bfa', fontWeight: '600' }}> intelligent optimization</span> in one powerful system.
          </p>

          {/* Value props in a distinctive layout */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            maxWidth: '900px',
            margin: '40px auto',
            padding: '0 20px'
          }}>
            {[
              { icon: '⚡', text: 'Script AI workflows with BAIQL' },
              { icon: '🎯', text: '11 AI models, one interface' },
              { icon: '💰', text: 'Pay per token, not per month' }
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(167, 139, 250, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.6)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>{item.icon}</div>
                <div style={{ fontSize: '1rem', color: '#e0e6ff' }}>{item.text}</div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            marginTop: '50px',
            flexWrap: 'wrap'
          }}>
            <button onClick={handleGetStarted} style={{
              padding: '18px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
            }}>
              Start Building For Free
            </button>
            
            <button onClick={handleLearnPricing} style={{
              padding: '18px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              background: 'transparent',
              color: '#a78bfa',
              border: '2px solid #a78bfa',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(167, 139, 250, 0.1)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              View Pricing
            </button>
          </div>

          {/* Trust indicator */}
          <p style={{
            textAlign: 'center',
            marginTop: '30px',
            fontSize: '0.9rem',
            color: '#9ca3af',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            No credit card required • Professional & Enterprise plans available
          </p>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.15; }
            50% { opacity: 0.25; }
          }
        `}</style>
      </section>

      {/* "Not Like The Others" Section */}
      <section style={{
        background: '#0a0a0a',
        padding: '100px 20px',
        borderTop: '1px solid #1f1f1f'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '20px',
            color: '#ffffff'
          }}>
            We're Not Like The Others
          </h2>
          
          <p style={{
            fontSize: '1.3rem',
            textAlign: 'center',
            color: '#9ca3af',
            maxWidth: '800px',
            margin: '0 auto 60px auto',
            lineHeight: '1.6'
          }}>
            ChatGPT is a chatbot. Zapier connects apps. Claude and Gemini are locked ecosystems. 
            <span style={{ color: '#a78bfa', fontWeight: '600' }}> We're the AI operating system</span> that brings it all together.
          </p>

          {/* Comparison Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2px',
            background: 'rgba(167, 139, 250, 0.2)',
            borderRadius: '16px',
            overflow: 'hidden'
          }}>
            {[
              {
                title: 'Traditional AI Tools',
                items: [
                  { text: 'Single model lock-in', icon: '🔒' },
                  { text: '$20-100/month subscriptions', icon: '💸' },
                  { text: 'Manual copy-paste workflows', icon: '📋' },
                  { text: 'No automation capabilities', icon: '❌' },
                  { text: 'Hidden usage limits', icon: '⚠️' }
                ],
                highlight: false
              },
              {
                title: 'PocketPE Platform',
                items: [
                  { text: '11 models, switch anytime', icon: '🎯' },
                  { text: 'Pay-per-use, $5 minimum', icon: '💰' },
                  { text: 'BAIQL script automation', icon: '⚡' },
                  { text: 'Visual + code interfaces', icon: '🚀' },
                  { text: 'Transparent token pricing', icon: '📊' }
                ],
                highlight: true
              }
            ].map((column, idx) => (
              <div key={idx} style={{
                background: column.highlight ? 'linear-gradient(135deg, #1a0b2e 0%, #2d1b4e 100%)' : '#111111',
                padding: '40px 30px',
                border: column.highlight ? '2px solid #764ba2' : 'none'
              }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  marginBottom: '30px',
                  color: column.highlight ? '#a78bfa' : '#ffffff',
                  textAlign: 'center'
                }}>
                  {column.title}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {column.items.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '1rem',
                      color: '#e0e6ff'
                    }}>
                      <span style={{ fontSize: '1.3rem' }}>{item.icon}</span>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features Deep Dive */}
      <section style={{
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a0b2e 100%)',
        padding: '100px 20px',
        borderTop: '1px solid #1f1f1f'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '20px',
            color: '#ffffff'
          }}>
            Five Superpowers. One Platform.
          </h2>

          <p style={{
            fontSize: '1.2rem',
            textAlign: 'center',
            color: '#9ca3af',
            maxWidth: '700px',
            margin: '0 auto 60px auto'
          }}>
            Everything you need to harness AI at scale, from solo projects to enterprise deployments.
          </p>

          {/* Feature Tabs */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '40px',
            overflowX: 'auto',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {features.map((feature, idx) => (
              <button
                key={idx}
                onClick={() => setActiveFeature(idx)}
                style={{
                  padding: '12px 24px',
                  background: activeFeature === idx 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'rgba(255, 255, 255, 0.05)',
                  color: activeFeature === idx ? '#ffffff' : '#9ca3af',
                  border: activeFeature === idx ? 'none' : '1px solid rgba(167, 139, 250, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (activeFeature !== idx) {
                    e.currentTarget.style.background = 'rgba(167, 139, 250, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeFeature !== idx) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.3)';
                  }
                }}
              >
                {feature.icon} {feature.title}
              </button>
            ))}
          </div>

          {/* Active Feature Display */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(167, 139, 250, 0.2)',
            borderRadius: '16px',
            padding: '50px 40px',
            backdropFilter: 'blur(10px)',
            minHeight: '300px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr',
              gap: '40px',
              alignItems: 'center'
            }}>
              {/* Left side - Icon and highlight */}
              <div style={{
                textAlign: 'center',
                padding: '30px'
              }}>
                <div style={{
                  fontSize: '6rem',
                  marginBottom: '20px',
                  filter: 'drop-shadow(0 4px 20px rgba(102, 126, 234, 0.5))'
                }}>
                  {features[activeFeature].icon}
                </div>
                <div style={{
                  display: 'inline-block',
                  padding: '8px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#ffffff'
                }}>
                  {features[activeFeature].highlight}
                </div>
              </div>

              {/* Right side - Content */}
              <div>
                <h3 style={{
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  marginBottom: '10px',
                  color: '#ffffff'
                }}>
                  {features[activeFeature].title}
                </h3>
                <p style={{
                  fontSize: '1.2rem',
                  color: '#a78bfa',
                  marginBottom: '20px',
                  fontWeight: '500'
                }}>
                  {features[activeFeature].subtitle}
                </p>
                <p style={{
                  fontSize: '1.1rem',
                  color: '#e0e6ff',
                  lineHeight: '1.7'
                }}>
                  {features[activeFeature].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section - For All Audiences */}
      <section style={{
        background: '#0a0a0a',
        padding: '100px 20px',
        borderTop: '1px solid #1f1f1f'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '60px',
            color: '#ffffff'
          }}>
            Built For Everyone
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '30px'
          }}>
            {[
              {
                audience: 'Professionals',
                icon: '👤',
                useCases: [
                  'Optimize prompts for better results',
                  'Access 11 AI models, choose the best',
                  'Automate repetitive AI tasks',
                  'Pay only for what you use'
                ],
                price: 'From $5'
              },
              {
                audience: 'Teams',
                icon: '👥',
                useCases: [
                  'Share BAIQL scripts across team',
                  'Standardize AI workflows',
                  'Track usage and costs per user',
                  'Collaborate on complex prompts'
                ],
                price: 'Custom'
              },
              {
                audience: 'Enterprise',
                icon: '🏢',
                useCases: [
                  'Real-time monitoring & alerts',
                  'PII detection & content moderation',
                  'Role-based permissions',
                  'Dedicated support & SLAs'
                ],
                price: 'Custom'
              }
            ].map((segment, idx) => (
              <div key={idx} style={{
                background: 'linear-gradient(135deg, rgba(26, 11, 46, 0.6) 0%, rgba(45, 27, 78, 0.6) 100%)',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                borderRadius: '16px',
                padding: '40px 30px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.7)';
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '20px', textAlign: 'center' }}>
                  {segment.icon}
                </div>
                <h3 style={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  marginBottom: '20px',
                  color: '#ffffff',
                  textAlign: 'center'
                }}>
                  {segment.audience}
                </h3>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  marginBottom: '25px'
                }}>
                  {segment.useCases.map((useCase, i) => (
                    <li key={i} style={{
                      fontSize: '1rem',
                      color: '#e0e6ff',
                      marginBottom: '12px',
                      paddingLeft: '25px',
                      position: 'relative',
                      lineHeight: '1.5'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        color: '#a78bfa'
                      }}>✓</span>
                      {useCase}
                    </li>
                  ))}
                </ul>
                <div style={{
                  textAlign: 'center',
                  padding: '12px',
                  background: 'rgba(167, 139, 250, 0.1)',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#a78bfa'
                }}>
                  {segment.price}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{
        background: 'linear-gradient(135deg, #1a0b2e 0%, #2d1b4e 100%)',
        padding: '100px 20px',
        textAlign: 'center',
        borderTop: '1px solid rgba(167, 139, 250, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background glow effect */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(102, 126, 234, 0.3) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '700',
            marginBottom: '30px',
            color: '#ffffff',
            lineHeight: '1.2'
          }}>
            Ready To Build Something<br />The World Hasn't Seen?
          </h2>

          <p style={{
            fontSize: '1.3rem',
            color: '#e0e6ff',
            marginBottom: '50px',
            lineHeight: '1.6'
          }}>
            Join the platform that's redefining what's possible with AI automation.<br />
            No credit card. No commitments. Just pure potential.
          </p>

          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button onClick={handleGetStarted} style={{
              padding: '20px 50px',
              fontSize: '1.2rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 30px rgba(102, 126, 234, 0.5)',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.7)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(102, 126, 234, 0.5)';
            }}>
              Start Building Now
            </button>
          </div>

          <p style={{
            marginTop: '30px',
            fontSize: '0.95rem',
            color: '#9ca3af',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            Free account includes Professional features • Upgrade anytime
          </p>
        </div>
      </section>

      {/* Versioning Info Footer */}
      <footer style={{
        background: '#0a0a0a',
        padding: '30px 20px',
        textAlign: 'center',
        borderTop: '1px solid #1f1f1f'
      }}>
        <p style={{
          fontSize: '0.9rem',
          color: 'rgba(224, 230, 255, 0.4)',
          margin: 0,
          fontFamily: '"JetBrains Mono", monospace',
          background: 'linear-gradient(135deg, rgba(224, 230, 255, 0.7) 0%, rgba(167, 139, 250, 0.5) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          PocketPE © 2025 V1.0.0
        </p>
      </footer>
    </div>
  );
};